import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Common } from "@/components/common";
import { Icon } from "@/components/Icons";
import {
  Table,
  Td,
  Th,
  CustomScrollbar,
  Pagination,
  ModalContent,
} from "@/components";
import { useAppDispatch, useAppSelector } from "@/sharedStore/hooks";
import { PATH } from "@/configuration/globalVariable";
import {
  fetchExclusiveLists,
  setKeyword,
  setPagination,
} from "./providers/slice";
import {
  useCreateExclusiveList,
  useDeleteExclusiveList,
  useGetAssignedCustomers,
} from "./services/exclusiveLists";
import { SnackbarUtils } from "@/utils";
import { AssignedCustomerItem } from "./models";

const tableHeaderClasses = "bg-neutral-20 font-bold";
const tableCellClasses =
  "!whitespace-normal border-b border-neutral-20 px-2 py-1";

interface SettingsMenuState {
  listId: string;
  listName: string;
}

const ExclusiveListsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const lists = useAppSelector((s) => s.exclusiveListsReducer.lists);
  const loading = useAppSelector((s) => s.exclusiveListsReducer.loading);
  const keyword = useAppSelector((s) => s.exclusiveListsReducer.keyword);
  const pagination = useAppSelector((s) => s.exclusiveListsReducer.pagination);

  const [newListName, setNewListName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Settings dropdown
  const [settingsMenu, setSettingsMenu] = useState<SettingsMenuState | null>(
    null
  );
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  // Customer count dialog
  const [customersDialog, setCustomersDialog] = useState<{
    listId: string;
    listName: string;
  } | null>(null);
  const [assignedCustomers, setAssignedCustomers] = useState<
    AssignedCustomerItem[]
  >([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const createMuta = useCreateExclusiveList();
  const deleteMuta = useDeleteExclusiveList();
  const getAssignedCustomersMuta = useGetAssignedCustomers();

  const load = (page?: number) => {
    dispatch(
      fetchExclusiveLists({
        keyword,
        pageNumber: page ?? pagination.pageNumber,
      })
    );
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(e.target as Node)
      ) {
        setSettingsMenu(null);
      }
    };
    if (settingsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [settingsMenu]);

  const handleSearch = () => {
    load(1);
  };

  const openModal = () => {
    setNewListName("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setNewListName("");
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    const res = await createMuta.mutateAsync(newListName.trim());
    if (res?.data?.success) {
      SnackbarUtils.success("Exclusive list created.");
      navigate(
        `/${PATH.EXCLUSIVE_LISTS._}/${PATH.EXCLUSIVE_LISTS.DETAIL}?id=${res.data?.id}`
      );
    }
  };

  const handleDelete = (id: string, name: string) => {
    setConfirmDelete({ id, name });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const res = await deleteMuta.mutateAsync(confirmDelete.id);
    if (res?.data?.success) {
      SnackbarUtils.success("Exclusive list deleted.");
      setConfirmDelete(null);
      load(pagination.pageNumber);
    }
  };

  const handleOpenCustomersDialog = async (
    listId: string,
    listName: string
  ) => {
    setCustomersDialog({ listId, listName });
    setAssignedCustomers([]);
    setLoadingCustomers(true);
    try {
      const res = await getAssignedCustomersMuta.mutateAsync(listId);
      setAssignedCustomers(res?.data || []);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const pageTotal = Math.ceil(pagination.total / pagination.pageSize) || 1;

  return (
    <div className="flex flex-col p-4 bg-white rounded shadow h-full">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 mb-4 shadow-underline">
        <h5 className="text-hd5 font-medium">Exclusive Lists</h5>
        <Common.Button
          size="lg"
          iconType="fill"
          iconLeft={Icon.PlusBorder}
          onClick={openModal}
        >
          New list
        </Common.Button>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-72">
          <Common.Input
            iconRight={Icon.Search}
            iconType="stroke"
            placeholder="Search by list name..."
            value={keyword}
            onChange={(e) => dispatch(setKeyword(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            onClear={() => {
              dispatch(setKeyword(""));
              dispatch(fetchExclusiveLists({ keyword: "", pageNumber: 1 }));
            }}
          />
        </div>
        <Common.Button size="lg" outline onClick={handleSearch}>
          Search
        </Common.Button>
      </div>

      {/* Table */}
      <div className="flex flex-col flex-1 min-h-0">
        <CustomScrollbar>
          <Table className="relative w-full border border-line border-separate border-spacing-0">
            <thead>
              <tr>
                <Th className={tableHeaderClasses}>Name</Th>
                <Th className={`${tableHeaderClasses} w-[15%]`}>
                  Product count
                </Th>
                <Th className={`${tableHeaderClasses} w-[15%]`}>
                  Customer count
                </Th>
                <Th className={`${tableHeaderClasses} w-[20%]`}>
                  Last updated
                </Th>
                <Th className={`${tableHeaderClasses} w-[10%] text-center`}>
                  Actions
                </Th>
              </tr>
            </thead>
            <tbody>
              {/* Loading */}
              {loading && (
                <tr>
                  <Td colSpan={5}>
                    <Icon.Loading size={60} className="mx-auto my-36" />
                  </Td>
                </tr>
              )}

              {/* Empty state */}
              {!loading && lists.length === 0 && (
                <tr>
                  <Td colSpan={5} className="text-center py-8 text-neutral-60">
                    No exclusive lists found.
                  </Td>
                </tr>
              )}

              {/* Data rows */}
              {!loading &&
                lists.map((list) => (
                  <tr key={list.id} className="hover:bg-neutral-5">
                    <Td className={`${tableCellClasses} font-medium`}>
                      {list.name}
                    </Td>
                    <Td className={tableCellClasses}>{list.productCount}</Td>
                    <Td className={tableCellClasses}>
                      <button
                        className="text-blue-600 underline"
                        onClick={() =>
                          handleOpenCustomersDialog(list.id, list.name)
                        }
                      >
                        {list.customerCount ?? 0}
                      </button>
                    </Td>
                    <Td className={tableCellClasses}>
                      {list.updatedDateFormatted}
                    </Td>
                    <Td className={`${tableCellClasses} text-center`}>
                      <div className="flex items-center justify-center gap-1">
                        {/* Settings dropdown */}
                        <div
                          className="relative"
                          ref={
                            settingsMenu?.listId === list.id
                              ? settingsMenuRef
                              : undefined
                          }
                        >
                          <Common.Button
                            size="sm"
                            variant="outline"
                            iconType="fill"
                            iconLeft={Icon.Settings}
                            outline
                            className="bg-transparent border-transparent hover:!border-transparent"
                            onClick={() =>
                              setSettingsMenu((prev) =>
                                prev?.listId === list.id
                                  ? null
                                  : { listId: list.id, listName: list.name }
                              )
                            }
                          />
                          {settingsMenu?.listId === list.id && (
                            <div className="absolute right-0 z-50 mt-1 w-44 bg-white border border-neutral-20 rounded shadow-lg py-1">
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-5 flex items-center gap-2"
                                onClick={() => {
                                  setSettingsMenu(null);
                                  navigate(
                                    `/${PATH.EXCLUSIVE_LISTS._}/${PATH.EXCLUSIVE_LISTS.DETAIL}?id=${list.id}`
                                  );
                                }}
                              >
                                <Icon.Edit
                                  iconType="stroke"
                                  size="14"
                                  className="shrink-0"
                                />
                                Edit items
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-5 flex items-center gap-2"
                                onClick={() => {
                                  setSettingsMenu(null);
                                  navigate(
                                    `/${PATH.EXCLUSIVE_LISTS._}/${PATH.EXCLUSIVE_LISTS.ASSIGN_CUSTOMERS}?id=${list.id}`
                                  );
                                }}
                              >
                                <Icon.Team
                                  iconType="fill"
                                  size="14"
                                  className="shrink-0"
                                />
                                Assign customers
                              </button>
                            </div>
                          )}
                        </div>

                        <Common.Button
                          variant="outline"
                          color="red"
                          size="md"
                          iconType="fill"
                          iconLeft={Icon.Trash}
                          outline
                          className="bg-transparent border-transparent hover:!border-transparent"
                          onClick={() => handleDelete(list.id, list.name)}
                          isLoading={deleteMuta.isLoading}
                        />
                      </div>
                    </Td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </CustomScrollbar>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="mt-2">
            <Pagination
              pageNumber={pagination.pageNumber}
              pageSize={pagination.pageSize}
              total={pagination.total}
              totalPages={pageTotal}
              pagerCenterPoint={pagination.pageNumber}
              startPage={1}
              onPageClick={(page: number) => {
                dispatch(setPagination({ pageNumber: page }));
                load(page);
              }}
            />
          </div>
        )}
      </div>

      {/* New list modal */}
      <ModalContent
        open={showModal}
        onClose={closeModal}
        onOverlayClick={closeModal}
        initialFocus={inputRef}
        panelClassWidth="sm:max-w-md sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-4">New exclusive list</h3>
          <Common.Input
            ref={inputRef}
            label="List name"
            placeholder="Enter list name..."
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateList();
              if (e.key === "Escape") closeModal();
            }}
          />
          <div className="flex justify-end gap-2 mt-6">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={closeModal}
              disabled={createMuta.isLoading}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="blue"
              size="lg"
              onClick={handleCreateList}
              isLoading={createMuta.isLoading}
              disabled={!newListName.trim() || createMuta.isLoading}
            >
              Create
            </Common.Button>
          </div>
        </div>
      </ModalContent>

      {/* Confirm delete modal */}
      <ModalContent
        open={!!confirmDelete}
        onOverlayClick={() => setConfirmDelete(null)}
        panelClassWidth="sm:max-w-sm sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-2">Delete exclusive list</h3>
          <p className="text-neutral-60 mb-6">
            Are you sure you want to delete &quot;{confirmDelete?.name}&quot;?
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={() => setConfirmDelete(null)}
              disabled={deleteMuta.isLoading}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="red"
              size="lg"
              onClick={handleConfirmDelete}
              isLoading={deleteMuta.isLoading}
            >
              Delete
            </Common.Button>
          </div>
        </div>
      </ModalContent>

      {/* Assigned customers dialog */}
      <ModalContent
        open={!!customersDialog}
        onClose={() => setCustomersDialog(null)}
        onOverlayClick={() => setCustomersDialog(null)}
        panelClassWidth="sm:max-w-2xl sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-4">
            Customers assigned to &quot;{customersDialog?.listName}&quot;
          </h3>

          {loadingCustomers ? (
            <Icon.Loading size={60} className="mx-auto my-12" />
          ) : (
            <Table className="w-full border border-line border-separate border-spacing-0">
              <thead>
                <tr>
                  <Th className={tableHeaderClasses}>Name</Th>
                  <Th className={`${tableHeaderClasses} w-[12%]`}>Code</Th>
                  <Th className={`${tableHeaderClasses} w-[10%]`}>Status</Th>
                  <Th className={`${tableHeaderClasses} w-[20%]`}>Email</Th>
                </tr>
              </thead>
              <tbody>
                {assignedCustomers.length === 0 && (
                  <tr>
                    <Td
                      colSpan={4}
                      className="text-center py-8 text-neutral-60"
                    >
                      No customers assigned to this list.
                    </Td>
                  </tr>
                )}
                {assignedCustomers.map((c) => (
                  <tr key={c.accountId} className="hover:bg-neutral-5">
                    <Td className={tableCellClasses}>{c.tradingName}</Td>
                    <Td className={`${tableCellClasses} text-neutral-60`}>
                      {c.customerCode}
                    </Td>
                    <Td className={tableCellClasses}>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          c.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {c.isActive ? "Active" : "Inactive"}
                      </span>
                    </Td>
                    <Td className={tableCellClasses}>{c.email}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className="flex justify-end mt-6">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={() => setCustomersDialog(null)}
            >
              Close
            </Common.Button>
          </div>
        </div>
      </ModalContent>
    </div>
  );
};

export default ExclusiveListsPage;
